import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Button,
  Switch,
  FormControlLabel,
  useTheme,
  SelectChangeEvent
} from '@mui/material';
import { FilterAltOff } from '@mui/icons-material';
import { NotificationFilters as NotificationFiltersType } from '@/store/NotificationStore';

interface NotificationFiltersProps {
  filters: NotificationFiltersType;
  onFilterChange: (filters: Partial<NotificationFiltersType>) => void;
  onReset: () => void;
}

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' }
];

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'birthday', label: 'Birthday' },
  { value: 'work_anniversary', label: 'Work Anniversary' },
  { value: 'probation_ending', label: 'Probation Ending' },
  { value: 'certification_expiring', label: 'Certification Expiring' },
  { value: 'leave_balance_low', label: 'Leave Balance Low' },
  { value: 'document_expiring', label: 'Document Expiring' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'custom', label: 'Custom' }
];

const NotificationFilters: React.FC<NotificationFiltersProps> = ({
  filters,
  onFilterChange,
  onReset
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handlePriorityChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onFilterChange({
      priority: value ? value as NotificationFiltersType['priority'] : undefined
    });
  };

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onFilterChange({
      alertType: value || undefined
    });
  };

  const handleReadStatusChange = (_: React.SyntheticEvent, newValue: string) => {
    onFilterChange({
      isRead: newValue === 'all' ? undefined : newValue === 'read'
    });
  };

  const handleIncludeDismissedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      includeDismissed: event.target.checked
    });
  };

  const getReadStatusValue = () => {
    if (filters.isRead === undefined) return 'all';
    return filters.isRead ? 'read' : 'unread';
  };

  const hasActiveFilters =
    filters.priority !== undefined ||
    filters.alertType !== undefined ||
    filters.isRead !== undefined ||
    filters.includeDismissed;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        gap: 2,
        p: 2,
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
        borderRadius: 2,
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
      }}
    >
      {/* Read Status Tabs */}
      <Tabs
        value={getReadStatusValue()}
        onChange={handleReadStatusChange}
        sx={{
          minHeight: 36,
          '& .MuiTab-root': {
            minHeight: 36,
            py: 0,
            px: 2,
            fontSize: '0.8125rem',
            textTransform: 'none'
          }
        }}
      >
        <Tab value="all" label="All" />
        <Tab value="unread" label="Unread" />
        <Tab value="read" label="Read" />
      </Tabs>

      {/* Priority Filter */}
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Priority</InputLabel>
        <Select
          value={filters.priority || ''}
          label="Priority"
          onChange={handlePriorityChange}
        >
          {PRIORITY_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Type Filter */}
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Type</InputLabel>
        <Select
          value={filters.alertType || ''}
          label="Type"
          onChange={handleTypeChange}
        >
          {TYPE_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Include Dismissed Toggle */}
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={filters.includeDismissed}
            onChange={handleIncludeDismissedChange}
          />
        }
        label="Include dismissed"
        sx={{
          ml: { xs: 0, md: 1 },
          '& .MuiFormControlLabel-label': {
            fontSize: '0.8125rem'
          }
        }}
      />

      {/* Reset Button */}
      {hasActiveFilters && (
        <Button
          size="small"
          startIcon={<FilterAltOff />}
          onClick={onReset}
          sx={{
            ml: { xs: 0, md: 'auto' },
            textTransform: 'none'
          }}
        >
          Reset Filters
        </Button>
      )}
    </Box>
  );
};

export default NotificationFilters;
